import { useEffect, useRef, useState } from "react";
import { useMenu, UseMenuProps } from "../../../hook/menu";
import { useSelectedNotebookPageIdMap } from "../../../hook/page";
import { useFolderPrimaryKey, useFetchFolderPrimaryKey } from "../../../hook/page/property";
import { useAppIdMap } from "../../../hook/app";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import { CasLoader } from "../../brand/CasLoader";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  UploadCsvMenuContext,
  useUploadCsvMenuContext,
} from "../../../hook/menu/database/uploadCsvMenu";
import { UploadCsvMenuContextValue } from "../../../types/menu/database/uploadCsvMenu";
import { writeTable } from "../../../api/table";

const Papa = require("papaparse");

export const UploadCsvMenuProvider: React.FC<{
  children?: React.ReactNode;
  useMenuProps: UseMenuProps;
}> = ({ children, useMenuProps }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { folderPKey, setFolderPKey } = useFolderPrimaryKey(selectedNotebookPageId);
  const fetchPKey = useFetchFolderPrimaryKey();
  useEffect(() => {
    if (selectedNotebookPageId) fetchPKey(selectedNotebookPageId);
  }, [selectedNotebookPageId]);
  const primaryKey = folderPKey ?? [];
  const canSubmit = Boolean(uploadResult) && !error;
  const ctxValue: UploadCsvMenuContextValue = {
    useMenuProps,
    anchorEl,
    setAnchorEl,
    isLoading,
    setIsLoading,
    error,
    setError,
    uploadResult,
    setUploadResult,
    fileInputRef,
    canSubmit,
    primaryKey,
    setPrimaryKey: setFolderPKey,
  };

  return (
    <UploadCsvMenuContext.Provider value={ctxValue}>
      {children}
    </UploadCsvMenuContext.Provider>
  );
};

export const UploadCsvMenuSelectFileButton: React.FC = () => {
  const { uploadResult, fileInputRef, setError, isLoading, error } =
    useUploadCsvMenuContext();

  const parsedOk = Boolean(uploadResult) && !error;

  const handleMenuItemClick = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography variant="body2">Select CSV File</Typography>
      <Box sx={{ position: "relative" }}>
        <IconButton
          onClick={handleMenuItemClick}
          disabled={isLoading}
          color={parsedOk ? "success" : "primary"}
        >
          {parsedOk ? <CheckCircleIcon /> : <UploadFileIcon />}
        </IconButton>
        {isLoading && (
          <CasLoader
            size={34}
            sx={{ position: "absolute", top: 3, left: 3 }}
          />
        )}
      </Box>
    </Box>
  );
};

export const UploadCsvMenuFileInput: React.FC = () => {
  const { fileInputRef, setIsLoading, setError, setUploadResult, primaryKey } =
    useUploadCsvMenuContext();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsLoading(true);
      setError(null);
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: (results: any) => {
          const header = results.data[0].map((h: any) => String(h));
          const rows = results.data
            .slice(1)
            .map((row: any) => row.map((el: any) => (el === null ? "" : el)));
          setUploadResult({ header, rows });
          const missing = primaryKey.filter((pk) => !header.includes(pk));
          if (missing.length > 0) {
            setError(
              "This CSV is missing the table's primary key column(s): " +
                missing.join(", ") +
                ". Add them or change the table's primary key, then re-upload.",
            );
          } else {
            setError(null);
          }
          setIsLoading(false);
        },
        error: (error: any) => {
          console.error("CSV Parsing Error:", error.message);
          setError("Failed to parse CSV file.");
          setIsLoading(false);
        },
      });
    }
  };

  return (
    <input
      type="file"
      ref={fileInputRef}
      onChange={handleFileChange}
      style={{ display: "none" }}
      accept=".csv"
    />
  );
};

export const UploadCsvMenuAlert: React.FC = () => {
  const { error } = useUploadCsvMenuContext();

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mt: 1, fontSize: "0.75rem" }}>
          {error}
        </Alert>
      )}
    </>
  );
};

export const UploadCsvSubmitButton: React.FC = () => {
  const { isLoading, setIsLoading, uploadResult, canSubmit, useMenuProps, setError } =
    useUploadCsvMenuContext();
  const { onClose } = useMenuProps;

  const { appId } = useAppIdMap();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();

  const handleUploadSubmit = () => {
    if (!canSubmit) return;
    const newUploadResult = { ...uploadResult };
    setIsLoading(true);
    // console.log(
    //   JSON.stringify(
    //     JSON.stringify({
    //       "app-id": appId!,
    //       "file-id": selectedNotebookPageId!,
    //       data: newUploadResult,
    //     }),
    //   ),
    // );
    writeTable({
      "app-id": appId!,
      "file-id": selectedNotebookPageId!,
      data: uploadResult,
    }).then((e) => {
      setIsLoading(false);
      onClose();
    }).catch((e) => {
      setIsLoading(false);
      setError(typeof e?.message === "string" ? e.message : "Upload failed. Please check the table and try again.");
    });
  };

  return (
    <Button
      fullWidth
      variant="contained"
      disabled={!canSubmit}
      onClick={handleUploadSubmit}
    >
      {isLoading ? "Saving..." : "Finish Import"}
    </Button>
  );
};

export const UploadCsvHasDataPortion: React.FC = () => {
  const { uploadResult } = useUploadCsvMenuContext();

  return (
    <>
      {uploadResult && (
        <>
          <Divider sx={{ my: 1 }} />
          <UploadCsvSubmitButton />
        </>
      )}
    </>
  );
};

export const UploadCsvMenuMenuContainer: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const { useMenuProps } = useUploadCsvMenuContext();
  return (
    <Popover
      {...useMenuProps}
      anchorOrigin={{ horizontal: "left", vertical: "center" }}
      transformOrigin={{ horizontal: "right", vertical: "center" }}
    >
      <Box
        sx={{
          width: "15rem",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {children}
      </Box>
    </Popover>
  );
};

export const UploadCsvMenuInternal: React.FC = () => {
  const { setAnchorEl } = useUploadCsvMenuContext();

  const menuAnchorRef = useRef<HTMLDivElement>(null);

  // useEffect(() => {
  //   if (menuAnchorRef.current) {
  //     setAnchorEl(menuAnchorRef.current);
  //   }
  // }, [menuAnchorRef.current]);

  return (
    <Box ref={menuAnchorRef} sx={{ display: "inline-block" }}>
      <UploadCsvMenuFileInput />
      <UploadCsvMenuMenuContainer>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, display: "flex" }}
        >
          Upload Data Source
        </Typography>
        <Divider sx={{ display: "flex" }} />
        <UploadCsvMenuSelectFileButton />
        <UploadCsvHasDataPortion />
        <UploadCsvMenuAlert />
      </UploadCsvMenuMenuContainer>
    </Box>
  );
};

export const UploadCsvMenu: React.FC<UseMenuProps> = ({ ...useMenuProps }) => {
  return (
    <UploadCsvMenuProvider useMenuProps={useMenuProps}>
      <UploadCsvMenuInternal />
    </UploadCsvMenuProvider>
  );
};
